import { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useI18n } from '../i18n'
import { useAuthStore } from '../store/useAuthStore'
import { get as apiGet, post as apiPost } from '../lib/api'
import { SECTION_TYPES, INSTRUMENT_TYPES } from '../config/songbook'
import { GuitarTab } from '../components/songbook/GuitarTab'
import { GuitarTablature } from '../components/songbook/GuitarTablature'
import { MidiPlayer as MidiPlayerShared } from '../components/songbook/MidiPlayer'
import { useCachedImage } from '../lib/offline/useCachedImage'
import { openAttachment } from '../lib/offline/audioPlayback'
import { OnlineIndicator } from '../components/offline/OnlineIndicator'
import { AudioCachePrompt } from '../components/offline/AudioCachePrompt'
import { ListenCachePrompt } from '../components/offline/ListenCachePrompt'
import { OFFLINE_ENABLED } from '../config/offline'
import { cacheSongData } from '../lib/offline/autoCache'
import { useCachedAudioSrc } from '../lib/offline/listenCache'
import {
  loadSongsByShareToken,
  loadSharedCollectionMeta,
  saveSharedCollectionMeta,
} from '../lib/offline/songCache'
import type { Song, Collection, SongElement, SectionGroup } from '../types/songbook'

function mapSong(r: Record<string, unknown>): Song {
  return {
    id: r.id as string,
    userId: r.user_id as string,
    collectionId: (r.collection_id as string) ?? null,
    title: r.title as string,
    notes: (r.notes as string) ?? '',
    info: (r.info as string) ?? '',
    listenUrl: (r.listen_url as string) ?? '',
    duration: (r.duration as number) ?? null,
    shareToken: (r.share_token as string) ?? null,
    attachments: (r.attachments ?? []) as Song['attachments'],
    elements: (r.elements ?? []) as SongElement[],
    createdAt: r.created_at as string,
    updatedAt: r.updated_at as string,
  }
}

export function ShareViewPage({ mode = 'collection' }: { mode?: 'collection' | 'song' }) {
  const { t } = useI18n()
  const { token } = useParams<{ token: string }>()
  const [collection, setCollection] = useState<Collection | null>(null)
  const [songs, setSongs] = useState<Song[]>([])
  const [selectedSong, setSelectedSong] = useState<Song | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) return

    let cancelled = false

    async function tryOfflineFirst(): Promise<boolean> {
      if (!OFFLINE_ENABLED) return false

      const cachedSongs = await loadSongsByShareToken(token!)
      if (cachedSongs.length === 0) return false
      if (cancelled) return true

      if (mode === 'song') {
        const first = cachedSongs[0]
        setSongs([first])
        setSelectedSong(first)
        setIsLoading(false)
        return true
      }

      const meta = await loadSharedCollectionMeta(token!)
      if (cancelled) return true

      setCollection({
        id: 'offline',
        userId: cachedSongs[0].userId,
        name: meta?.name ?? 'Offline-Sammlung',
        artist: meta?.artist ?? '',
        shareToken: token ?? null,
        createdAt: '',
        updatedAt: '',
      })
      setSongs(cachedSongs)
      setIsLoading(false)
      return true
    }

    async function fetchFromCloud(hadCachedHit: boolean) {
      if (mode === 'song') {
        try {
          const data = await apiGet<Record<string, unknown>>(`/share/song/${token}`)
          if (cancelled) return
          const song = mapSong(data)
          setSongs([song])
          setSelectedSong(song)
          setIsLoading(false)
        } catch {
          if (cancelled) return
          if (!hadCachedHit) { setError('Not found'); setIsLoading(false) }
        }
        return
      }

      // Collection share
      try {
        const data = await apiGet<{
          collection: Record<string, unknown>
          songs: Record<string, unknown>[]
        }>(`/share/collection/${token}`)
        if (cancelled) return

        const col: Collection = {
          id:         data.collection.id as string,
          userId:     data.collection.user_id as string,
          name:       data.collection.name as string,
          artist:     (data.collection.artist as string) ?? '',
          shareToken: (data.collection.share_token as string) ?? null,
          createdAt:  data.collection.created_at as string,
          updatedAt:  data.collection.updated_at as string,
        }

        const mappedSongs = data.songs.map(mapSong)
        setCollection(col)
        setSongs(mappedSongs)
        setIsLoading(false)

        // Cache the WHOLE collection offline, not just the song the user
        // happens to open. Every member is stored under the collection
        // share token so loadSongsByShareToken(token) returns all of them.
        if (OFFLINE_ENABLED && token) {
          void saveSharedCollectionMeta(token, { name: col.name, artist: col.artist })
          mappedSongs.forEach((song) => {
            void cacheSongData(song, 'shared', token)
          })
        }
      } catch {
        if (cancelled) return
        if (!hadCachedHit) { setError('Not found'); setIsLoading(false) }
      }
    }

    async function load() {
      const hadCachedHit = await tryOfflineFirst()
      if (cancelled) return
      // Always try the network too — refreshes stale offline data
      // when there is connectivity. Failures are silently ignored
      // when we already showed something from the cache.
      await fetchFromCloud(hadCachedHit)
    }

    if (!OFFLINE_ENABLED) {
      setIsLoading(true)
    }
    void load()

    return () => {
      cancelled = true
    }
  }, [token, mode])

  // Auto-cache the selected song (data + images) so the next visit
  // is instant even without network. Audio is opt-in via the prompt.
  useEffect(() => {
    if (!OFFLINE_ENABLED) return
    if (!selectedSong) return
    // Always key the cached song by the URL share token, so a collection
    // member never gets re-stored under its own (possibly different) token,
    // which would drop it from loadSongsByShareToken(collectionToken).
    void cacheSongData(selectedSong, 'shared', token ?? selectedSong.shareToken ?? null)
  }, [selectedSong, token])

  if (isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <p className="text-text-muted">{t('general.loading')}</p>
      </div>
    )
  }

  if (error || (!collection && !selectedSong)) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <p className="text-text-muted">Link nicht gefunden.</p>
      </div>
    )
  }

  // Single song mode — show directly
  if (mode === 'song' && selectedSong) {
    return (
      <div className="flex min-h-full flex-col">
        <header className="px-4 py-4">
          <h1 className="flex items-center gap-2 text-xl font-bold text-text">
            {selectedSong.title || t('songbook.untitled')}
            <OnlineIndicator />
          </h1>
        </header>
        <main className="flex-1 px-4 pb-8">
          <SharedSongDetail song={selectedSong} t={t} />
        </main>
      </div>
    )
  }

  // Collection mode
  return (
    <div className="flex min-h-full flex-col">
      <header className="px-4 py-4">
        <h1 className="flex items-center gap-2 text-xl font-bold text-text">
          {collection!.name}
          <OnlineIndicator />
        </h1>
        {collection!.artist && (
          <p className="text-sm text-text-muted">{collection!.artist}</p>
        )}
        <p className="mt-1 text-xs text-text-muted">
          {t('songbook.songsCount', { count: String(songs.length) })}
        </p>
      </header>

      <main className="flex-1 px-4 pb-8">
        {!selectedSong ? (
          <div className="space-y-2">
            {songs.map((song, idx) => (
              <button
                key={song.id}
                className="flex w-full items-center gap-3 rounded-lg border border-surface-lighter bg-surface-light p-3 text-left active:bg-surface-lighter"
                onClick={() => setSelectedSong(song)}
              >
                <span className="text-sm text-text-muted">{idx + 1}.</span>
                <span className="font-medium text-text">
                  {song.title || t('songbook.untitled')}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div>
            <button
              className="mb-4 flex items-center gap-1 text-sm text-primary"
              onClick={() => setSelectedSong(null)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"/></svg>
              {t('songbook.back')}
            </button>
            <SharedSongDetail song={selectedSong} t={t} />
          </div>
        )}
      </main>
    </div>
  )
}

/* Shared song detail (used by both modes) */
function SharedSongDetail({ song, t }: { song: Song; t: ReturnType<typeof useI18n>['t'] }) {
  const cachedListenSrc = useCachedAudioSrc(song.listenUrl)

  return (
    <>
      <h2 className="mb-3 text-lg font-bold text-text">
        {song.title || t('songbook.untitled')}
      </h2>

      <ForkSongBanner song={song} />

      <AudioCachePrompt song={song} />

      {song.info && (
        <div
          className="mini-editor mb-3 rounded-lg bg-surface-light p-3 text-sm text-text-muted"
          dangerouslySetInnerHTML={{ __html: song.info }}
        />
      )}

      {song.notes && (
        <p className="mb-3 whitespace-pre-wrap rounded-lg bg-surface-light p-3 text-sm text-text-muted">
          {song.notes}
        </p>
      )}

      {song.listenUrl && (
        <div className="mb-4">
          <audio src={cachedListenSrc} controls className="w-full rounded-lg" style={{ height: '40px' }} />
          <ListenCachePrompt url={song.listenUrl} />
        </div>
      )}

      {song.attachments.length > 0 && (
        <div className="mb-4 space-y-1">
          <p className="text-xs font-medium text-text-muted">{t('songbook.files')}</p>
          {song.attachments.map((att) => (
            <button
              key={att.id}
              className="flex w-full items-center gap-2 rounded border border-surface-lighter p-2 text-left text-sm text-text hover:bg-surface-lighter"
              onClick={() => openAttachment(att.storagePath)}
            >
              <span>{/\.(mp3|ogg|wav|flac|aac|m4a)$/i.test(att.filename) ? '🎵' : /\.mid/i.test(att.filename) ? '🎹' : '📄'}</span>
              <span className="truncate">{att.filename}</span>
            </button>
          ))}
        </div>
      )}

      <SharedSongContent elements={song.elements} t={t} />
    </>
  )
}

/* Read-only rendering of song elements */
function SharedSongContent({ elements, t }: { elements: SongElement[]; t: ReturnType<typeof useI18n>['t'] }) {
  const groups = useMemo((): SectionGroup[] => {
    const result: SectionGroup[] = []
    for (const el of elements) {
      if (el.type === 'section') {
        result.push({ section: el, tracks: [] })
      } else if (el.type === 'instrument' && result.length > 0) {
        result[result.length - 1].tracks.push({ instrument: el, children: [] })
      } else if ((el.type === 'chord' || el.type === 'tab' || el.type === 'tablature' || el.type === 'image' || el.type === 'midi') && result.length > 0) {
        const group = result[result.length - 1]
        if (group.tracks.length > 0) {
          group.tracks[group.tracks.length - 1].children.push(el)
        } else {
          group.tracks.push({
            instrument: {
              id: '_default_' + group.section.id,
              type: 'instrument',
              instrumentTypeId: '_default',
              number: 0,
            },
            children: [el],
          })
        }
      }
    }
    return result
  }, [elements])

  // Honour each section's saved collapsed flag so a shared song opens the
  // way the author left it in the editor. Viewers can still expand any
  // section by tapping its header.
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() => {
    const ids = new Set<string>()
    for (const el of elements) {
      if (el.type === 'section' && el.collapsed) ids.add(el.id)
    }
    return ids
  })

  const toggleSection = (id: string) => setCollapsedIds((prev) => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  return (
    <div className="space-y-3">
      {groups.map((group) => {
        const cfg = SECTION_TYPES.find((s) => s.id === group.section.sectionTypeId)
        const color = cfg?.color ?? 'bg-surface-lighter'
        const label = group.section.customName || (cfg ? t(cfg.i18nKey as Parameters<typeof t>[0]) : group.section.sectionTypeId)

        const collapsed = collapsedIds.has(group.section.id)
        // Same rule as the editor: arrow only when expanding reveals content.
        const namedTracks = group.tracks.filter((tr) => tr.instrument.instrumentTypeId !== '_default')
        const hasDefaultContent = group.tracks.some((tr) => tr.instrument.instrumentTypeId === '_default' && tr.children.length > 0)
        const hasContent = namedTracks.length > 0 || hasDefaultContent

        return (
          <div key={group.section.id} className="rounded-lg border border-surface-lighter bg-surface-light">
            <div className="cursor-pointer p-3 pb-1" onClick={() => toggleSection(group.section.id)}>
              <span className="inline-flex flex-wrap items-center gap-1.5">
                <span className={`inline-block rounded px-2 py-0.5 text-sm font-semibold text-white ${color}`}>
                  {hasContent && (
                    <span className="mr-1 inline-block text-[10px] transition-transform" style={{ transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▼</span>
                  )}
                  {label} {group.section.number}
                </span>
                {collapsed && namedTracks.map((tr) => {
                  const instCfg = INSTRUMENT_TYPES.find((i) => i.id === tr.instrument.instrumentTypeId)
                  const instLabel = tr.instrument.customName
                    || (instCfg ? t(instCfg.i18nKey as Parameters<typeof t>[0]) : tr.instrument.instrumentTypeId)
                  return (
                    <span key={tr.instrument.id} className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] leading-none text-white/60">
                      {instLabel}
                    </span>
                  )
                })}
                {collapsed && hasDefaultContent && namedTracks.length === 0 && (
                  <span className="text-[10px] leading-none text-white/50">•</span>
                )}
              </span>
              {group.section.note && (
                <p className="mt-1 text-xs italic text-text-muted">{group.section.note}</p>
              )}
            </div>

            {!collapsed && group.tracks.map((track) => {
              const isDefault = track.instrument.instrumentTypeId === '_default'
              const instCfg = INSTRUMENT_TYPES.find((i) => i.id === track.instrument.instrumentTypeId)
              const instLabel = track.instrument.customName
                || (instCfg ? t(instCfg.i18nKey as Parameters<typeof t>[0]) : track.instrument.instrumentTypeId)

              return (
                <div key={track.instrument.id} className={`${isDefault ? '' : 'border-t border-surface-lighter/50'} px-3 py-2`}>
                  {!isDefault && <p className="mb-1.5 text-xs text-text-muted">
                    <span className="font-medium text-text">{instLabel}</span>
                    {track.instrument.note && (
                      <span className="ml-1 italic"> — {track.instrument.note}</span>
                    )}
                  </p>}
                  {track.children.length > 0 && (
                    <div className="flex items-start gap-2 overflow-x-auto pb-1">
                      {track.children.map((child) => {
                        if (child.type === 'chord') {
                          return (
                            <div key={child.id} className="shrink-0">
                              <span className="inline-block rounded border border-surface-lighter px-2.5 py-1 text-base font-bold text-text">
                                {child.name.charAt(0).toUpperCase() + child.name.slice(1)}
                              </span>
                              {child.note && <p className="mt-0.5 text-[10px] italic text-text-muted">{child.note}</p>}
                            </div>
                          )
                        }
                        if (child.type === 'midi') {
                          return (
                            <div key={child.id} className="w-full">
                              <MidiPlayerShared element={child} />
                              {child.note && <p className="mt-0.5 text-[10px] italic text-text-muted">{child.note}</p>}
                            </div>
                          )
                        }
                        if (child.type === 'image') {
                          return <SharedImage key={child.id} element={child} />
                        }
                        if (child.type === 'tablature') {
                          return (
                            <div key={child.id} className="w-full">
                              <GuitarTablature columns={child.columns} />
                              {child.note && <p className="mt-0.5 text-[10px] italic text-text-muted">{child.note}</p>}
                            </div>
                          )
                        }
                        return (
                          <div key={child.id} className="shrink-0">
                            <GuitarTab dots={child.dots} startFret={child.startFret ?? 1} size="md" />
                            {child.note && <p className="mt-0.5 text-[10px] italic text-text-muted">{child.note}</p>}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

/* ── Fork banner — lets the viewer save their own editable copy ── */

function ForkSongBanner({ song }: { song: Song }) {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [isForking, setIsForking] = useState(false)

  async function handleFork() {
    if (!user) {
      navigate('/login')
      return
    }
    setIsForking(true)
    try {
      const id      = crypto.randomUUID()
      await apiPost('/songs', {
        id,
        title:         song.title,
        user_id:       user.id,
        collection_id: null,
        notes:         song.notes || null,
        info:          song.info  || null,
        listen_url:    song.listenUrl || null,
        elements:      song.elements,
      })
      navigate(`/songs/${id}`)
    } catch (e) {
      console.error('Fork failed:', e)
      setIsForking(false)
    }
  }

  return (
    <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-surface-lighter bg-surface-light px-3 py-2">
      <p className="text-xs text-text-muted">Eigene Notizen hinzufügen?</p>
      <button
        className="shrink-0 rounded bg-primary px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
        onClick={handleFork}
        disabled={isForking}
      >
        {isForking ? '...' : 'Als eigene Kopie speichern'}
      </button>
    </div>
  )
}

function SharedImage({ element }: { element: { storagePath: string; note?: string } }) {
  const url = useCachedImage(element.storagePath)

  if (!url) return null

  return (
    <div className="w-full">
      <img src={url} alt={element.note || ''} className="rounded" style={{ maxHeight: 500, width: 'auto' }} />
      {element.note && <p className="mt-0.5 text-[10px] italic text-text-muted">{element.note}</p>}
    </div>
  )
}
